import React, { useCallback, useState } from 'react';

import { APIUser, APIUserStatus } from '@cd3p/core/types/api';
import { Box, Button, Stack, SvgIcon, Switch, Typography } from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';

import { styled, useNavigate, useSelector, useTranslation } from 'third-party';

import {
  ONBOARDING_STEPS,
  allOrdersUrl,
  onboardingStepUrl,
} from 'constants/url';

import { useApp } from 'modules/app';

import { appSelectors, termsAndConditionsSelectors } from 'selectors';

const StyledButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 15px 0;
  background: none;
  border: none;
  border-bottom: 1px solid #ccc;
  text-align: left;
  color: #333;
  cursor: pointer;
`;

const CenteredContainer = styled(Stack)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1rem auto;
  padding: 1rem;
`;

const TermsButtonText = styled(Typography)`
  font-weight: bold;
`;

const Disclaimer = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  margin-top: 1rem;
`;

const AcceptButton = styled(Button)`
  width: 80%;
`;

export const TermsAndConditions: React.FC = () => {
  const { t } = useTranslation();
  const { updateCurrentUser } = useApp();
  const user = useSelector(appSelectors.user);
  const navigate = useNavigate();
  const [isAccepted, setIsAccepted] = useState(false);

  const handleApiResult = useHandleApiResult();

  const onAcceptButtonClick = useCallback(() => {
    handleApiResult<APIUser>(
      () =>
        updateCurrentUser({
          ...user,
          userStatus: APIUserStatus.Active,
        }),
      ({ result }) => {
        if (result.payload.userStatus === APIUserStatus.Incomplete) {
          navigate(onboardingStepUrl(ONBOARDING_STEPS.Step3), {
            replace: true,
          });
        } else {
          navigate(allOrdersUrl(), { replace: true });
        }
      },
    );
  }, [handleApiResult, navigate, updateCurrentUser, user]);

  const termsAndConditionsItemsLoaded = useSelector(
    termsAndConditionsSelectors.termsAndConditionsItemsLoaded,
  );
  const termsAndConditionsItemsTree = useSelector(
    termsAndConditionsSelectors.termsAndConditionsItemsTree,
  );

  return (
    <CenteredContainer>
      <Typography variant="body1">
        {t('termsAndConditions.panel.title')}
      </Typography>
      {termsAndConditionsItemsLoaded &&
        termsAndConditionsItemsTree.map(item => (
          <StyledButton
            key={item.id}
            onClick={() => {
              navigate(item.id);
            }}
          >
            <TermsButtonText variant="body1">{item.name}</TermsButtonText>
            <SvgIcon height="1.2rem" width="0.74rem" icon="shevron-right" />
          </StyledButton>
        ))}
      <Disclaimer>
        <Typography variant="body1">
          {t('termsAndConditions.panel.disclaimerTitle')}
        </Typography>
        <Switch
          sx={{ marginLeft: '2.5rem' }}
          onChange={e => setIsAccepted(e.target.checked)}
          checked={isAccepted}
        />
      </Disclaimer>
      <AcceptButton
        disabled={!isAccepted}
        onClick={onAcceptButtonClick}
        variant="contained"
        color="primary"
      >
        {t('termsAndConditions.panel.acceptButton')}
      </AcceptButton>
    </CenteredContainer>
  );
};
